import { apiUrl } from "../api/endpoints";
import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Spinner from "../Components/Spinner";
import { useAuth } from "../hooks/AuthProvider";
import { LanguageContext } from "../context/LanguageContext";
import { setAiReportTypesCache } from "../utils/aiReportTypes";
import { DEFAULT_ASSET_CARD_CONFIG, normalizeAssetCardConfig, normalizeStringList } from "../utils/assetCardConfig";
import {
  fetchVoiceMediaLibrary,
  normalizeAiChatVoiceReplyConfig,
  normalizeVoiceCommandConfig,
  notifyAiChatVoiceConfigUpdated,
  notifyBrowserVoiceCommandConfigUpdated,
  uploadVoiceMedia,
} from "../services/aiVoiceAssistantApi";
import {
  normalizeDashboardWidgetFontSizes,
  notifyDashboardWidgetFontSizesUpdated,
} from "../utils/dashboardWidgetFontSizes";
import { DEFAULT_CHART_SAMPLING_CONFIG, normalizeChartSamplingConfig } from "../utils/chartSamplingConfig";
import { getWebDashboardWidgetDefaults } from "./WebDashboard/layout/webDashboardWidgetDefaults";
import GoogleSheetsAssetSyncPanel from "./GoogleSheetsAssetSyncPanel";
import systemConfigTranslations from "../languages/systemConfigurations.json";
import "./SystemConfigurations.css";

import SystemConfigHeader from "./SystemConfigurations/SystemConfigHeader";
import SystemConfigTabs from "./SystemConfigurations/SystemConfigTabs";
import SystemConfigActionsBar from "./SystemConfigurations/SystemConfigActionsBar";
import ReportsConfigPanel from "./SystemConfigurations/ReportsConfigPanel";
import AssetCardsConfigPanel from "./SystemConfigurations/AssetCardsConfigPanel";
import DatastreamPrioritiesConfigPanel from "./SystemConfigurations/DatastreamPrioritiesConfigPanel";
import DashboardWidgetsConfigPanel from "./SystemConfigurations/DashboardWidgetsConfigPanel";
import ChartSamplingConfigPanel from "./SystemConfigurations/ChartSamplingConfigPanel";
import VoiceCommandsConfigPanel from "./SystemConfigurations/VoiceCommandsConfigPanel";
import AiChatVoiceConfigPanel from "./SystemConfigurations/AiChatVoiceConfigPanel";
import WeatherCronConfigPanel from "./SystemConfigurations/WeatherCronConfigPanel";
import FcmTokenCleanupConfigPanel from "./SystemConfigurations/FcmTokenCleanupConfigPanel";
import SubscriberPermissionsConfigPanel from "./SystemConfigurations/SubscriberPermissionsConfigPanel";
import AuditLogsConfigPanel from "./SystemConfigurations/AuditLogsConfigPanel";
import {
  createBlankAssetCategory,
  createBlankParam,
  createBlankReportType,
  createBlankVoiceAction,
  DEFAULT_AUDIT_LOG_POLICY,
  DEFAULT_REPORT_PRIORITY_POLICY,
  DASHBOARD_WIDGET_FONT_SIZE_FIELDS,
  modeIcons,
  normalizeAiReportAvailability,
  normalizeAuditLogPolicy,
  normalizeGoogleSheetsAssetSync,
  normalizeFcmTokenCleanupConfig,
  normalizeMode,
  normalizeNewSubscriberPermissionExclusions,
  normalizeReportPriorityPolicy,
  normalizeReportTypes,
  normalizeWeatherCronConfig,
  WIDGET_PREVIEW_DEFAULT_PROP_KEYS,
  WIDGET_PREVIEW_SIZES,
  WIDGET_PREVIEW_TITLES,
} from "./SystemConfigurations/systemConfigurationUtils";

const SystemConfigurations = () => {
  const { user } = useAuth();
  const { lang } = useContext(LanguageContext);
  const t = systemConfigTranslations[lang] || systemConfigTranslations.en;
  const isRtl = ["ar", "ur"].includes(lang);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [activeTab, setActiveTab] = useState("reports");

  const [mode, setMode] = useState("internal_pdf");
  const [savedMode, setSavedMode] = useState("internal_pdf");
  const [reportTypes, setReportTypes] = useState([]);
  const [savedReportTypes, setSavedReportTypes] = useState([]);
  const [aiReportAvailability, setAiReportAvailability] = useState(normalizeAiReportAvailability());
  const [savedAiReportAvailability, setSavedAiReportAvailability] = useState(normalizeAiReportAvailability());
  const [selectedTypeIndex, setSelectedTypeIndex] = useState(0);

  const [assetCardConfig, setAssetCardConfig] = useState(DEFAULT_ASSET_CARD_CONFIG);
  const [savedAssetCardConfig, setSavedAssetCardConfig] = useState(DEFAULT_ASSET_CARD_CONFIG);

  const [newSubscriberPermissionExclusions, setNewSubscriberPermissionExclusions] = useState(normalizeNewSubscriberPermissionExclusions());
  const [savedNewSubscriberPermissionExclusions, setSavedNewSubscriberPermissionExclusions] = useState(normalizeNewSubscriberPermissionExclusions());
  const [permissions, setPermissions] = useState([]);
  const [permissionSearch, setPermissionSearch] = useState("");

  const [aiVoiceCommands, setAiVoiceCommands] = useState(normalizeVoiceCommandConfig());
  const [savedAiVoiceCommands, setSavedAiVoiceCommands] = useState(normalizeVoiceCommandConfig());
  const [aiChatVoiceReply, setAiChatVoiceReply] = useState(normalizeAiChatVoiceReplyConfig());
  const [savedAiChatVoiceReply, setSavedAiChatVoiceReply] = useState(normalizeAiChatVoiceReplyConfig());
  const [voiceMediaLibrary, setVoiceMediaLibrary] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [selectedVoiceSubscriberId, setSelectedVoiceSubscriberId] = useState("default");
  const [selectedAiChatVoiceSubscriberId, setSelectedAiChatVoiceSubscriberId] = useState("all");
  const [uploadingVoiceActionIndex, setUploadingVoiceActionIndex] = useState(null);

  const [weatherCron, setWeatherCron] = useState(normalizeWeatherCronConfig());
  const [savedWeatherCron, setSavedWeatherCron] = useState(normalizeWeatherCronConfig());
  const [fcmTokenCleanup, setFcmTokenCleanup] = useState(normalizeFcmTokenCleanupConfig());
  const [savedFcmTokenCleanup, setSavedFcmTokenCleanup] = useState(normalizeFcmTokenCleanupConfig());

  const [dashboardWidgetFontSizes, setDashboardWidgetFontSizes] = useState(normalizeDashboardWidgetFontSizes());
  const [savedDashboardWidgetFontSizes, setSavedDashboardWidgetFontSizes] = useState(normalizeDashboardWidgetFontSizes());
  const [selectedDashboardWidgetType, setSelectedDashboardWidgetType] = useState(DASHBOARD_WIDGET_FONT_SIZE_FIELDS[0]?.type || "chart");

  const [chartSampling, setChartSampling] = useState(DEFAULT_CHART_SAMPLING_CONFIG);
  const [savedChartSampling, setSavedChartSampling] = useState(DEFAULT_CHART_SAMPLING_CONFIG);

  const [googleSheetsAssetSync, setGoogleSheetsAssetSync] = useState(normalizeGoogleSheetsAssetSync());
  const [savedGoogleSheetsAssetSync, setSavedGoogleSheetsAssetSync] = useState(normalizeGoogleSheetsAssetSync());

  const [reportPriorityPolicy, setReportPriorityPolicy] = useState(DEFAULT_REPORT_PRIORITY_POLICY);
  const [savedReportPriorityPolicy, setSavedReportPriorityPolicy] = useState(DEFAULT_REPORT_PRIORITY_POLICY);

  const [auditLogPolicy, setAuditLogPolicy] = useState(DEFAULT_AUDIT_LOG_POLICY);
  const [savedAuditLogPolicy, setSavedAuditLogPolicy] = useState(DEFAULT_AUDIT_LOG_POLICY);

  const isSuper = String(user?.role || "").toLowerCase() === "super";
  const isDirty =
    mode !== savedMode ||
    JSON.stringify(reportTypes) !== JSON.stringify(savedReportTypes) ||
    JSON.stringify(aiReportAvailability) !== JSON.stringify(savedAiReportAvailability) ||
    JSON.stringify(assetCardConfig) !== JSON.stringify(savedAssetCardConfig) ||
    JSON.stringify(newSubscriberPermissionExclusions) !== JSON.stringify(savedNewSubscriberPermissionExclusions) ||
    JSON.stringify(aiVoiceCommands) !== JSON.stringify(savedAiVoiceCommands) ||
    JSON.stringify(aiChatVoiceReply) !== JSON.stringify(savedAiChatVoiceReply) ||
    JSON.stringify(weatherCron) !== JSON.stringify(savedWeatherCron) ||
    JSON.stringify(fcmTokenCleanup) !== JSON.stringify(savedFcmTokenCleanup) ||
    JSON.stringify(dashboardWidgetFontSizes) !== JSON.stringify(savedDashboardWidgetFontSizes) ||
    JSON.stringify(chartSampling) !== JSON.stringify(savedChartSampling) ||
    JSON.stringify(googleSheetsAssetSync) !== JSON.stringify(savedGoogleSheetsAssetSync) ||
    reportPriorityPolicy !== savedReportPriorityPolicy ||
    JSON.stringify(auditLogPolicy) !== JSON.stringify(savedAuditLogPolicy);

  const downloadModes = useMemo(
    () => ({
      internal_pdf: { ...t.healthModes.internal_pdf, icon: modeIcons.internal_pdf },
      anovator_redirect: { ...t.healthModes.anovator_redirect, icon: modeIcons.anovator_redirect },
    }),
    [t.healthModes]
  );

  const dashboardWidgetDefaults = useMemo(() => getWebDashboardWidgetDefaults({}, {}), []);
  const selectedDashboardWidgetField =
    DASHBOARD_WIDGET_FONT_SIZE_FIELDS.find((field) => field.type === selectedDashboardWidgetType) ||
    DASHBOARD_WIDGET_FONT_SIZE_FIELDS[0];
  const selectedDashboardWidgetSizes = (selectedDashboardWidgetField?.fields || []).reduce((acc, field) => {
    const rawValue = dashboardWidgetFontSizes.widgets?.[selectedDashboardWidgetField.type]?.[field.key];
    acc[field.key] = rawValue === "" || rawValue === null || rawValue === undefined ? field.value : rawValue;
    return acc;
  }, {});
  const selectedDashboardWidgetProps = {
    ...(dashboardWidgetDefaults[WIDGET_PREVIEW_DEFAULT_PROP_KEYS[selectedDashboardWidgetField?.type]] || {}),
    ...selectedDashboardWidgetSizes,
  };
  const selectedDashboardWidgetSize =
    WIDGET_PREVIEW_SIZES[selectedDashboardWidgetField?.type] || { width: 300, height: 180 };
  const selectedDashboardWidgetTitle =
    WIDGET_PREVIEW_TITLES[selectedDashboardWidgetField?.type] ||
    selectedDashboardWidgetField?.label ||
    "Widget";

  useEffect(() => {
    const fetchConfig = async () => {
      if (!isSuper) {
        setLoading(false);
        return;
      }

      try {
        const res = await axios.get(apiUrl("configuration/system"));
        const nextMode = normalizeMode(res.data?.data?.healthReport?.value?.downloadMode);
        const nextReportTypes = normalizeReportTypes(res.data?.data?.aiReportTypes?.value);
        const nextAvailability = normalizeAiReportAvailability(res.data?.data?.aiReportAvailability?.value);
        const nextAssetCardConfig = normalizeAssetCardConfig(res.data?.data?.assetCard?.value);
        const nextNewSubscriberPermissionExclusions = normalizeNewSubscriberPermissionExclusions(res.data?.data?.newSubscriberPermissionExclusions?.value);
        const nextAiVoiceCommands = normalizeVoiceCommandConfig(res.data?.data?.aiVoiceCommands?.value);
        const nextAiChatVoiceReply = normalizeAiChatVoiceReplyConfig(res.data?.data?.aiChatVoiceReply?.value);
        const nextWeatherCron = normalizeWeatherCronConfig(res.data?.data?.weatherCron?.value);
        const nextFcmTokenCleanup = normalizeFcmTokenCleanupConfig(res.data?.data?.fcmTokenCleanup?.value);
        const nextDashboardWidgetFontSizes = normalizeDashboardWidgetFontSizes(res.data?.data?.dashboardWidgetFontSizes?.value);
        const nextChartSampling = normalizeChartSamplingConfig(res.data?.data?.chartSampling?.value);
        const nextGoogleSheetsAssetSync = normalizeGoogleSheetsAssetSync(res.data?.data?.googleSheetsAssetSync?.value);
        const nextReportPriorityPolicy = normalizeReportPriorityPolicy(res.data?.data?.reportPriorityPolicy?.value);
        const nextAuditLogPolicy = normalizeAuditLogPolicy(res.data?.data?.auditLogPolicy?.value);
        const [permissionsRes, mediaItems, subscribersRes] = await Promise.all([
          axios.get(apiUrl("permission/list")),
          fetchVoiceMediaLibrary().catch(() => []),
          axios.get(apiUrl("subscriber")).catch(() => ({ data: { data: [] } })),
        ]);

        setPermissions(Array.isArray(permissionsRes.data?.data) ? permissionsRes.data.data : []);
        setVoiceMediaLibrary(mediaItems);
        setSubscribers(Array.isArray(subscribersRes.data?.data) ? subscribersRes.data.data : []);

        setMode(nextMode);
        setSavedMode(nextMode);
        setReportTypes(nextReportTypes);
        setSavedReportTypes(nextReportTypes);
        setAiReportAvailability(nextAvailability);
        setSavedAiReportAvailability(nextAvailability);
        setAssetCardConfig(nextAssetCardConfig);
        setSavedAssetCardConfig(nextAssetCardConfig);
        setNewSubscriberPermissionExclusions(nextNewSubscriberPermissionExclusions);
        setSavedNewSubscriberPermissionExclusions(nextNewSubscriberPermissionExclusions);
        setAiVoiceCommands(nextAiVoiceCommands);
        setSavedAiVoiceCommands(nextAiVoiceCommands);
        setAiChatVoiceReply(nextAiChatVoiceReply);
        setSavedAiChatVoiceReply(nextAiChatVoiceReply);
        setWeatherCron(nextWeatherCron);
        setSavedWeatherCron(nextWeatherCron);
        setFcmTokenCleanup(nextFcmTokenCleanup);
        setSavedFcmTokenCleanup(nextFcmTokenCleanup);
        setDashboardWidgetFontSizes(nextDashboardWidgetFontSizes);
        setSavedDashboardWidgetFontSizes(nextDashboardWidgetFontSizes);
        setChartSampling(nextChartSampling);
        setSavedChartSampling(nextChartSampling);
        setGoogleSheetsAssetSync(nextGoogleSheetsAssetSync);
        setSavedGoogleSheetsAssetSync(nextGoogleSheetsAssetSync);
        setReportPriorityPolicy(nextReportPriorityPolicy);
        setSavedReportPriorityPolicy(nextReportPriorityPolicy);
        setAuditLogPolicy(nextAuditLogPolicy);
        setSavedAuditLogPolicy(nextAuditLogPolicy);
        setSelectedTypeIndex(0);
      } catch (err) {
        console.error(err);
        setMessage(t.loadError);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [isSuper, t.loadError]);

  const currentModeLabel = downloadModes[savedMode]?.title || t.healthModes.internal_pdf.title;
  const selectedType = reportTypes[selectedTypeIndex] || null;

  const save = async () => {
    setSaving(true);
    setMessage("");
    try {
      const res = await axios.put(apiUrl("configuration/system"), {
        healthReport: { downloadMode: mode },
        aiReportTypes: { types: reportTypes },
        aiReportAvailability,
        assetCard: assetCardConfig,
        newSubscriberPermissionExclusions,
        aiVoiceCommands,
        aiChatVoiceReply,
        weatherCron,
        fcmTokenCleanup,
        dashboardWidgetFontSizes,
        chartSampling,
        googleSheetsAssetSync,
        reportPriorityPolicy: { conflictPolicy: reportPriorityPolicy },
        auditLogPolicy,
      });

      const nextMode = normalizeMode(res.data?.data?.healthReport?.value?.downloadMode);
      const nextReportTypes = normalizeReportTypes(res.data?.data?.aiReportTypes?.value);
      const nextAvailability = normalizeAiReportAvailability(res.data?.data?.aiReportAvailability?.value);
      const nextAssetCardConfig = normalizeAssetCardConfig(res.data?.data?.assetCard?.value);
      const nextNewSubscriberPermissionExclusions = normalizeNewSubscriberPermissionExclusions(res.data?.data?.newSubscriberPermissionExclusions?.value);
      const nextAiVoiceCommands = normalizeVoiceCommandConfig(res.data?.data?.aiVoiceCommands?.value);
      const nextAiChatVoiceReply = normalizeAiChatVoiceReplyConfig(res.data?.data?.aiChatVoiceReply?.value);
      const nextWeatherCron = normalizeWeatherCronConfig(res.data?.data?.weatherCron?.value);
      const nextFcmTokenCleanup = normalizeFcmTokenCleanupConfig(res.data?.data?.fcmTokenCleanup?.value);
      const nextDashboardWidgetFontSizes = normalizeDashboardWidgetFontSizes(res.data?.data?.dashboardWidgetFontSizes?.value);
      const nextChartSampling = normalizeChartSamplingConfig(res.data?.data?.chartSampling?.value);
      const nextGoogleSheetsAssetSync = normalizeGoogleSheetsAssetSync(res.data?.data?.googleSheetsAssetSync?.value);
      const nextReportPriorityPolicy = normalizeReportPriorityPolicy(res.data?.data?.reportPriorityPolicy?.value);
      const nextAuditLogPolicy = normalizeAuditLogPolicy(res.data?.data?.auditLogPolicy?.value);

      setMode(nextMode);
      setSavedMode(nextMode);
      setReportTypes(nextReportTypes);
      setSavedReportTypes(nextReportTypes);
      setAiReportAvailability(nextAvailability);
      setSavedAiReportAvailability(nextAvailability);
      setAssetCardConfig(nextAssetCardConfig);
      setSavedAssetCardConfig(nextAssetCardConfig);
      setNewSubscriberPermissionExclusions(nextNewSubscriberPermissionExclusions);
      setSavedNewSubscriberPermissionExclusions(nextNewSubscriberPermissionExclusions);
      setAiVoiceCommands(nextAiVoiceCommands);
      setSavedAiVoiceCommands(nextAiVoiceCommands);
      setAiChatVoiceReply(nextAiChatVoiceReply);
      setSavedAiChatVoiceReply(nextAiChatVoiceReply);
      setWeatherCron(nextWeatherCron);
      setSavedWeatherCron(nextWeatherCron);
      setFcmTokenCleanup(nextFcmTokenCleanup);
      setSavedFcmTokenCleanup(nextFcmTokenCleanup);
      setDashboardWidgetFontSizes(nextDashboardWidgetFontSizes);
      setSavedDashboardWidgetFontSizes(nextDashboardWidgetFontSizes);
      setChartSampling(nextChartSampling);
      setSavedChartSampling(nextChartSampling);
      setGoogleSheetsAssetSync(nextGoogleSheetsAssetSync);
      setSavedGoogleSheetsAssetSync(nextGoogleSheetsAssetSync);
      setReportPriorityPolicy(nextReportPriorityPolicy);
      setSavedReportPriorityPolicy(nextReportPriorityPolicy);
      setAuditLogPolicy(nextAuditLogPolicy);
      setSavedAuditLogPolicy(nextAuditLogPolicy);
      notifyDashboardWidgetFontSizesUpdated(nextDashboardWidgetFontSizes);
      notifyBrowserVoiceCommandConfigUpdated(nextAiVoiceCommands);
      notifyAiChatVoiceConfigUpdated(nextAiChatVoiceReply);
      setAiReportTypesCache(nextReportTypes, nextAvailability);
      setSelectedTypeIndex(Math.min(selectedTypeIndex, Math.max(0, nextReportTypes.length - 1)));
      setMessage(t.saveSuccess);
      return true;
    } catch (err) {
      console.error(err);
      setMessage(err.response?.data?.message || err.response?.data?.error || t.saveError);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const updateAiReportAvailability = (key, checked) => {
    setAiReportAvailability((prev) => ({ ...prev, [key]: checked }));
  };

  const updateType = (index, patch) => {
    setReportTypes((prev) => prev.map((type, idx) => (idx === index ? { ...type, ...patch } : type)));
  };

  const addType = () => {
    setReportTypes((prev) => {
      const next = [...prev, createBlankReportType()];
      setSelectedTypeIndex(next.length - 1);
      return next;
    });
  };

  const deleteType = (index) => {
    setReportTypes((prev) => prev.filter((_, idx) => idx !== index));
    setSelectedTypeIndex((prev) => Math.max(0, prev - 1));
  };

  const updateParam = (typeIndex, paramIndex, patch) => {
    setReportTypes((prev) =>
      prev.map((type, idx) =>
        idx === typeIndex
          ? {
              ...type,
              params: type.params.map((param, pIdx) =>
                pIdx === paramIndex ? { ...param, ...patch } : param
              ),
            }
          : type
      )
    );
  };

  const addParam = (typeIndex) => {
    setReportTypes((prev) =>
      prev.map((type, idx) =>
        idx === typeIndex ? { ...type, params: [...type.params, createBlankParam()] } : type
      )
    );
  };

  const deleteParam = (typeIndex, paramIndex) => {
    setReportTypes((prev) =>
      prev.map((type, idx) =>
        idx === typeIndex
          ? { ...type, params: type.params.filter((_, pIdx) => pIdx !== paramIndex) }
          : type
      )
    );
  };

  const updateAssetCategory = (index, patch) => {
    setAssetCardConfig((prev) => ({
      ...prev,
      categories: prev.categories.map((category, idx) => (idx === index ? { ...category, ...patch } : category)),
    }));
  };

  const updateAssetCategoryTypes = (index, value) => {
    updateAssetCategory(index, { assetTypes: normalizeStringList(String(value || "").split(",")) });
  };

  const addAssetCategory = () => {
    setAssetCardConfig((prev) => ({ ...prev, categories: [...prev.categories, createBlankAssetCategory()] }));
  };

  const deleteAssetCategory = (index) => {
    setAssetCardConfig((prev) => ({ ...prev, categories: prev.categories.filter((_, idx) => idx !== index) }));
  };

  const updateAssetDeviceDetailsMaxItems = (value) => {
    const nextValue = Number(value);
    setAssetCardConfig((prev) => ({
      ...prev,
      deviceDetailsMaxItems: Number.isFinite(nextValue)
        ? Math.min(8, Math.max(1, Math.trunc(nextValue)))
        : DEFAULT_ASSET_CARD_CONFIG.deviceDetailsMaxItems,
    }));
  };

  const toggleAssetDeviceDetailsField = (fieldValue) => {
    setAssetCardConfig((prev) => {
      const current = new Set(prev.deviceDetailsFields || DEFAULT_ASSET_CARD_CONFIG.deviceDetailsFields);
      if (current.has(fieldValue)) {
        current.delete(fieldValue);
      } else {
        current.add(fieldValue);
      }
      const nextFields = Array.from(current);
      return {
        ...prev,
        deviceDetailsFields: nextFields.length ? nextFields : DEFAULT_ASSET_CARD_CONFIG.deviceDetailsFields,
      };
    });
  };

  const excludedPermissionIdSet = useMemo(
    () => new Set(newSubscriberPermissionExclusions.excludedPermissionIds || []),
    [newSubscriberPermissionExclusions]
  );

  const filteredPermissions = useMemo(() => {
    const term = permissionSearch.trim().toLowerCase();
    if (!term) return permissions;
    return permissions.filter((permission) =>
      [permission.name, permission.ids, permission.category, permission.subcategory]
        .map((value) => String(value || "").toLowerCase())
        .some((value) => value.includes(term))
    );
  }, [permissions, permissionSearch]);

  const toggleExcludedPermission = (permissionId) => {
    const id = String(permissionId || "").trim();
    if (!id) return;
    setNewSubscriberPermissionExclusions((prev) => {
      const current = new Set(prev.excludedPermissionIds || []);
      if (current.has(id)) current.delete(id);
      else current.add(id);
      return { excludedPermissionIds: Array.from(current).sort((a, b) => a.localeCompare(b)) };
    });
  };

  const clearExcludedPermissions = () => {
    setNewSubscriberPermissionExclusions({ excludedPermissionIds: [] });
  };

  const updateVoiceConfig = (patch) => {
    setAiVoiceCommands((prev) => normalizeVoiceCommandConfig({ ...prev, ...patch }));
  };

  const selectedVoiceSubscriberKey =
    selectedVoiceSubscriberId === "default" ? "" : String(selectedVoiceSubscriberId || "");
  const selectedVoiceActions = selectedVoiceSubscriberKey
    ? aiVoiceCommands.subscriberBasicActions?.[selectedVoiceSubscriberKey] || []
    : aiVoiceCommands.basicActions || [];
  const selectedVoiceSubscriber = subscribers.find(
    (subscriber) => String(subscriber.id) === selectedVoiceSubscriberKey
  );

  const updateSelectedVoiceActions = (updater) => {
    setAiVoiceCommands((prev) => {
      const current = selectedVoiceSubscriberKey
        ? prev.subscriberBasicActions?.[selectedVoiceSubscriberKey] || []
        : prev.basicActions || [];
      const nextActions = typeof updater === "function" ? updater(current) : updater;

      if (!selectedVoiceSubscriberKey) {
        return { ...prev, basicActions: nextActions };
      }

      return {
        ...prev,
        subscriberBasicActions: {
          ...(prev.subscriberBasicActions || {}),
          [selectedVoiceSubscriberKey]: nextActions,
        },
      };
    });
  };

  const updateVoiceBasicAction = (index, patch) => {
    updateSelectedVoiceActions((actions) =>
      actions.map((action, actionIndex) =>
        actionIndex === index ? { ...action, ...patch } : action
      )
    );
  };

  const updateVoiceBasicMedia = (index, mediaPatch) => {
    updateSelectedVoiceActions((actions) =>
      actions.map((action, actionIndex) =>
        actionIndex === index
          ? {
              ...action,
              action: {
                type: "media",
                media: { ...(action.action?.media || {}), ...mediaPatch },
              },
            }
          : action
      )
    );
  };

  const addVoiceBasicAction = () => {
    updateSelectedVoiceActions((actions) => [...actions, createBlankVoiceAction()]);
  };

  const deleteVoiceBasicAction = (index) => {
    updateSelectedVoiceActions((actions) => actions.filter((_, actionIndex) => actionIndex !== index));
  };

  const copyDefaultVoiceActionsToSubscriber = () => {
    if (!selectedVoiceSubscriberKey) return;
    updateSelectedVoiceActions(() =>
      (aiVoiceCommands.basicActions || []).map((action) => ({
        ...action,
        id: `${action.id || "voice"}-${selectedVoiceSubscriberKey}-${Date.now()}`,
      }))
    );
  };

  const clearSubscriberVoiceActions = () => {
    if (!selectedVoiceSubscriberKey) return;
    setAiVoiceCommands((prev) => {
      const nextSubscriberActions = { ...(prev.subscriberBasicActions || {}) };
      delete nextSubscriberActions[selectedVoiceSubscriberKey];
      return { ...prev, subscriberBasicActions: nextSubscriberActions };
    });
  };

  const handleVoiceMediaUpload = async (index, file) => {
    if (!file) return;
    setUploadingVoiceActionIndex(index);
    setMessage("");
    try {
      const uploaded = await uploadVoiceMedia({
        file,
        title: selectedVoiceActions[index]?.action?.media?.title || selectedVoiceActions[index]?.label || file.name,
      });
      const nextLibrary = await fetchVoiceMediaLibrary().catch(() =>
        uploaded ? [...voiceMediaLibrary, uploaded] : voiceMediaLibrary
      );
      setVoiceMediaLibrary(nextLibrary);
      if (uploaded?.path) {
        updateVoiceBasicMedia(index, {
          path: uploaded.path,
          title: uploaded.title || selectedVoiceActions[index]?.action?.media?.title || "",
          mediaType: uploaded.mediaType || "file",
        });
      }
      setMessage(t.voiceUploadSuccess || "Voice media uploaded and assigned.");
    } catch (err) {
      console.error(err);
      setMessage(err?.response?.data?.message || t.voiceUploadError || "Unable to upload voice media.");
    } finally {
      setUploadingVoiceActionIndex(null);
    }
  };

  const updateVoicePage = (index, patch) => {
    setAiVoiceCommands((prev) => ({
      ...prev,
      advanced: {
        ...prev.advanced,
        pages: prev.advanced.pages.map((page, pageIndex) => (pageIndex === index ? { ...page, ...patch } : page)),
      },
    }));
  };

  const updateVoiceEntity = (index, patch) => {
    setAiVoiceCommands((prev) => ({
      ...prev,
      advanced: {
        ...prev.advanced,
        entities: prev.advanced.entities.map((entity, entityIndex) =>
          entityIndex === index ? { ...entity, ...patch } : entity
        ),
      },
    }));
  };

  const updateWeatherCron = (patch) => {
    setWeatherCron((prev) => normalizeWeatherCronConfig({ ...prev, ...patch }));
  };

  const updateWeatherProvider = (providerId, patch) => {
    setWeatherCron((prev) =>
      normalizeWeatherCronConfig({
        ...prev,
        providers: prev.providers.map((provider) =>
          provider.id === providerId ? { ...provider, ...patch } : provider
        ),
      })
    );
  };

  const updateWeatherThreshold = (key, value) => {
    setWeatherCron((prev) =>
      normalizeWeatherCronConfig({
        ...prev,
        thresholds: { ...prev.thresholds, [key]: value },
      })
    );
  };

  const updateFcmTokenCleanup = (patch) => {
    setFcmTokenCleanup((prev) => normalizeFcmTokenCleanupConfig({ ...prev, ...patch }));
  };

  const updateDashboardWidgetFontSize = (type, key, value) => {
    const parsed = Number(value);
    setDashboardWidgetFontSizes((prev) => ({
      ...prev,
      widgets: {
        ...(prev.widgets || {}),
        [type]: {
          ...(prev.widgets?.[type] || {}),
          [key]: Number.isFinite(parsed) ? parsed : value,
        },
      },
    }));
  };

  const updateAuditLogPolicy = (patch) => {
    setAuditLogPolicy((prev) => normalizeAuditLogPolicy({ ...prev, ...patch }));
  };

  const toggleAuditLogAction = (action) => {
    setAuditLogPolicy((prev) => normalizeAuditLogPolicy({
      ...prev,
      actions: (prev.actions || DEFAULT_AUDIT_LOG_POLICY.actions).map((item) =>
        item.action === action ? { ...item, enabled: item.enabled === false } : item
      ),
    }));
  };

  const renderActivePanel = () => {
    if (activeTab === "reports") {
      return (
        <ReportsConfigPanel
          t={t}
          mode={mode}
          setMode={setMode}
          downloadModes={downloadModes}
          aiReportAvailability={aiReportAvailability}
          updateAiReportAvailability={updateAiReportAvailability}
          reportTypes={reportTypes}
          selectedType={selectedType}
          selectedTypeIndex={selectedTypeIndex}
          setSelectedTypeIndex={setSelectedTypeIndex}
          updateType={updateType}
          addType={addType}
          deleteType={deleteType}
          updateParam={updateParam}
          addParam={addParam}
          deleteParam={deleteParam}
        />
      );
    }

    if (activeTab === "assetCards") {
      return (
        <AssetCardsConfigPanel
          assetCardConfig={assetCardConfig}
          addAssetCategory={addAssetCategory}
          updateAssetCategory={updateAssetCategory}
          updateAssetCategoryTypes={updateAssetCategoryTypes}
          deleteAssetCategory={deleteAssetCategory}
          updateAssetDeviceDetailsMaxItems={updateAssetDeviceDetailsMaxItems}
          toggleAssetDeviceDetailsField={toggleAssetDeviceDetailsField}
        />
      );
    }

    if (activeTab === "datastreamPriorities") {
      return (
        <DatastreamPrioritiesConfigPanel
          t={t}
          reportPriorityPolicy={reportPriorityPolicy}
          setReportPriorityPolicy={setReportPriorityPolicy}
        />
      );
    }

    if (activeTab === "dashboardWidgets") {
      return (
        <DashboardWidgetsConfigPanel
          dashboardWidgetFontSizes={dashboardWidgetFontSizes}
          selectedDashboardWidgetField={selectedDashboardWidgetField}
          selectedDashboardWidgetTitle={selectedDashboardWidgetTitle}
          selectedDashboardWidgetSize={selectedDashboardWidgetSize}
          selectedDashboardWidgetProps={selectedDashboardWidgetProps}
          setSelectedDashboardWidgetType={setSelectedDashboardWidgetType}
          updateDashboardWidgetFontSize={updateDashboardWidgetFontSize}
        />
      );
    }

    if (activeTab === "chartSampling") {
      return (
        <ChartSamplingConfigPanel
          chartSampling={chartSampling}
          setChartSampling={setChartSampling}
        />
      );
    }

    if (activeTab === "voiceCommands") {
      return (
        <VoiceCommandsConfigPanel
          t={t}
          aiVoiceCommands={aiVoiceCommands}
          updateVoiceConfig={updateVoiceConfig}
          subscribers={subscribers}
          selectedVoiceSubscriberId={selectedVoiceSubscriberId}
          setSelectedVoiceSubscriberId={setSelectedVoiceSubscriberId}
          selectedVoiceSubscriberKey={selectedVoiceSubscriberKey}
          selectedVoiceSubscriber={selectedVoiceSubscriber}
          selectedVoiceActions={selectedVoiceActions}
          addVoiceBasicAction={addVoiceBasicAction}
          deleteVoiceBasicAction={deleteVoiceBasicAction}
          updateVoiceBasicAction={updateVoiceBasicAction}
          updateVoiceBasicMedia={updateVoiceBasicMedia}
          copyDefaultVoiceActionsToSubscriber={copyDefaultVoiceActionsToSubscriber}
          clearSubscriberVoiceActions={clearSubscriberVoiceActions}
          voiceMediaLibrary={voiceMediaLibrary}
          uploadingVoiceActionIndex={uploadingVoiceActionIndex}
          handleVoiceMediaUpload={handleVoiceMediaUpload}
          updateVoicePage={updateVoicePage}
          updateVoiceEntity={updateVoiceEntity}
        />
      );
    }

    if (activeTab === "aiChatVoice") {
      return (
        <AiChatVoiceConfigPanel
          value={aiChatVoiceReply}
          onChange={setAiChatVoiceReply}
          subscribers={subscribers}
          selectedSubscriberId={selectedAiChatVoiceSubscriberId}
          onSelectedSubscriberIdChange={setSelectedAiChatVoiceSubscriberId}
        />
      );
    }

    if (activeTab === "googleSheets") {
      return (
        <GoogleSheetsAssetSyncPanel
          value={googleSheetsAssetSync}
          subscribers={subscribers}
          onChange={setGoogleSheetsAssetSync}
          hasUnsavedChanges={isDirty}
          onSave={save}
        />
      );
    }

    if (activeTab === "weatherCron") {
      return (
        <WeatherCronConfigPanel
          weatherCron={weatherCron}
          updateWeatherCron={updateWeatherCron}
          updateWeatherProvider={updateWeatherProvider}
          updateWeatherThreshold={updateWeatherThreshold}
        />
      );
    }

    if (activeTab === "fcmTokenCleanup") {
      return (
        <FcmTokenCleanupConfigPanel
          fcmTokenCleanup={fcmTokenCleanup}
          updateFcmTokenCleanup={updateFcmTokenCleanup}
        />
      );
    }

    if (activeTab === "auditLogs") {
      return (
        <AuditLogsConfigPanel
          auditLogPolicy={auditLogPolicy}
          updateAuditLogPolicy={updateAuditLogPolicy}
          toggleAuditLogAction={toggleAuditLogAction}
        />
      );
    }

    return (
      <SubscriberPermissionsConfigPanel
        t={t}
        newSubscriberPermissionExclusions={newSubscriberPermissionExclusions}
        clearExcludedPermissions={clearExcludedPermissions}
        permissionSearch={permissionSearch}
        setPermissionSearch={setPermissionSearch}
        filteredPermissions={filteredPermissions}
        excludedPermissionIdSet={excludedPermissionIdSet}
        toggleExcludedPermission={toggleExcludedPermission}
      />
    );
  };

  if (loading) return <Spinner />;

  if (!isSuper) {
    return (
      <div className="configuration system-config-page" dir={isRtl ? "rtl" : "ltr"}>
        <div className="system-config-shell">
          <div className="system-config-empty">{t.noPermission}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="configuration system-config-page" dir={isRtl ? "rtl" : "ltr"}>
      <div className="system-config-shell">
        <SystemConfigHeader t={t} currentModeLabel={currentModeLabel} />
        <SystemConfigTabs activeTab={activeTab} onChange={setActiveTab} t={t} />
        {renderActivePanel()}
        <SystemConfigActionsBar
          message={message}
          isDirty={isDirty}
          saving={saving}
          onSave={save}
          t={t}
        />
      </div>
    </div>
  );
};

export default SystemConfigurations;
